import { useState, useEffect } from 'react'
import type { Pesquisa } from '../types'
import { api } from '../api'

const PESQUISA_CATEGORIAS = ['mercado', 'concorrente', 'tecnologia', 'regulamentacao', 'benchmark', 'fornecedor', 'geral']

export default function PesquisasPage() {
  const [pesquisas, setPesquisas] = useState<Pesquisa[]>([])
  const [selected, setSelected] = useState<Pesquisa | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = () => api.getPesquisas().then((p) => { setPesquisas(p); setLoading(false) })
  useEffect(() => { load() }, [])

  if (loading) return <div className="flex items-center justify-center h-full text-gray-400">Carregando...</div>

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Arquivo de Pesquisas</h1>
          <p className="text-sm text-gray-500">{pesquisas.length} pesquisa(s)</p>
        </div>
        <button onClick={() => { setSelected(null); setShowForm(true) }}
          className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + Nova Pesquisa
        </button>
      </div>

      <div className="space-y-4">
        {pesquisas.map((p) => (
          <div key={p.id} onClick={() => setSelected(p)}
            className="bg-white rounded-xl shadow-sm border p-5 cursor-pointer hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{p.categoria}</span>
              <span className="text-xs text-gray-400">
                {new Date(p.criado_em).toLocaleDateString('pt-BR')}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{p.titulo}</h3>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.resumo}</p>
            {p.fontes.length > 0 && (
              <p className="text-xs text-gray-400 mt-2">{p.fontes.length} fonte(s)</p>
            )}
          </div>
        ))}
        {pesquisas.length === 0 && (
          <p className="text-center text-gray-400 py-12">Nenhuma pesquisa arquivada ainda.</p>
        )}
      </div>

      {/* Detail */}
      {selected && !showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 overflow-auto" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{selected.categoria}</span>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-800">{selected.titulo}</h2>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-xs font-semibold text-blue-700 mb-1">Resumo</h3>
                <p className="text-sm text-gray-700">{selected.resumo}</p>
              </div>
              {selected.conteudo && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Conteudo</h3>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">{selected.conteudo}</div>
                </div>
              )}
              {selected.fontes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Fontes</h3>
                  <ul className="space-y-1">
                    {selected.fontes.map((f, i) => (
                      <li key={i} className="text-sm">
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline">{f.titulo}</a>
                        {f.data_acesso && <span className="text-xs text-gray-400 ml-2">({f.data_acesso})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Object.keys(selected.dados_chave).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Dados-Chave</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selected.dados_chave).map(([k, v]) => (
                      <div key={k} className="bg-gray-50 p-2 rounded">
                        <p className="text-[10px] text-gray-500 uppercase">{k.replace(/_/g, ' ')}</p>
                        <p className="text-sm font-medium text-gray-800">{String(v)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => { setShowForm(true) }} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Editar</button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <PesquisaForm
          pesquisa={selected}
          onClose={() => { setShowForm(false); setSelected(null) }}
          onSaved={() => { setShowForm(false); setSelected(null); load() }}
        />
      )}
    </div>
  )
}

function PesquisaForm({ pesquisa, onClose, onSaved }: {
  pesquisa: Pesquisa | null
  onClose: () => void
  onSaved: () => void
}) {
  const [titulo, setTitulo] = useState(pesquisa?.titulo ?? '')
  const [categoria, setCategoria] = useState(pesquisa?.categoria ?? 'geral')
  const [resumo, setResumo] = useState(pesquisa?.resumo ?? '')
  const [conteudo, setConteudo] = useState(pesquisa?.conteudo ?? '')
  const [fontes, setFontes] = useState(pesquisa?.fontes ?? [])
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!titulo.trim() || !resumo.trim()) return
    setSaving(true)
    try {
      const data = {
        titulo: titulo.trim(),
        categoria,
        resumo: resumo.trim(),
        conteudo: conteudo.trim() || undefined,
        fontes: fontes.filter((f) => f.titulo.trim() && f.url.trim()),
      }
      if (pesquisa) {
        await api.updatePesquisa(pesquisa.id, data)
      } else {
        await api.createPesquisa(data)
      }
      onSaved()
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{pesquisa ? 'Editar Pesquisa' : 'Nova Pesquisa'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Titulo..."
            className="w-full text-lg font-medium border-b pb-2 focus:outline-none focus:border-blue-500" />
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
            className="text-sm border rounded-lg p-2">
            {PESQUISA_CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Resumo executivo</label>
            <textarea value={resumo} onChange={(e) => setResumo(e.target.value)} rows={3}
              className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Conteudo completo</label>
            <textarea value={conteudo} onChange={(e) => setConteudo(e.target.value)} rows={8}
              className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-gray-500">Fontes</label>
              <button onClick={() => setFontes([...fontes, { titulo: '', url: '' }])}
                className="text-xs text-blue-600 hover:underline">+ Fonte</button>
            </div>
            {fontes.map((f, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={f.titulo} onChange={(e) => { const n = [...fontes]; n[i] = { ...n[i], titulo: e.target.value }; setFontes(n) }}
                  placeholder="Titulo" className="flex-1 text-sm border rounded p-1.5" />
                <input value={f.url} onChange={(e) => { const n = [...fontes]; n[i] = { ...n[i], url: e.target.value }; setFontes(n) }}
                  placeholder="URL" className="flex-1 text-sm border rounded p-1.5" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !titulo.trim() || !resumo.trim()}
            className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Salvando...' : pesquisa ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  )
}
